import { useEffect, useRef, useState } from "react";
import { PdfViewer } from "../../components";
import "./document-container.scss";
import { Badge, Collapse, Empty, Tabs } from "antd";
import { FileOutlined, TableOutlined ,DatabaseFilled} from "@ant-design/icons";
import TabPane from "antd/lib/tabs/TabPane";
import { Tooltip } from "antd/lib";
import { useDocumentReview } from "../../pages/Document";
import { SplitSummary } from "./components/split-summary/SplitSummary";
import { Insights } from "./components/insights/Insights";
import { AskAuto } from "./components/chat-container/AskAuto";
import { SplitDataItem, SplitDataType } from "./constants";
import { EvidenceContentJSON, IncomingContentJSON } from "../../components/pdf-viewer/JSONViewer";

const normalizeLookupKey = (value: string) => value.replace(/[^a-z0-9]/gi, "").toLowerCase();

const findValueByKeyCandidates = (source: any, candidates: string[]) => {
  const normalizedCandidates = candidates.map(normalizeLookupKey);
  const visited = new WeakSet<object>();

  const search = (node: any, depth: number): any => {
    if (node === null || node === undefined || depth > 6) {
      return undefined;
    }

    if (typeof node !== "object") {
      return undefined;
    }

    if (visited.has(node)) {
      return undefined;
    }

    visited.add(node);

    if (Array.isArray(node)) {
      for (const item of node) {
        const foundInArray = search(item, depth + 1);
        if (foundInArray !== undefined) {
          return foundInArray;
        }
      }

      return undefined;
    }

    const entries = Object.entries(node);

    for (const [key, value] of entries) {
      if (normalizedCandidates.includes(normalizeLookupKey(key))) {
        return value;
      }
    }

    for (const [, value] of entries) {
      const foundInObject = search(value, depth + 1);
      if (foundInObject !== undefined) {
        return foundInObject;
      }
    }

    return undefined;
  };

  return search(source, 0);
};

const toNumericValue = (value: any) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = parseFloat(value.replace("%", ""));
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const formatConfidence = (value: any) => {
  const numericValue = toNumericValue(value);
  if (numericValue === undefined) {
    return "Not available";
  }

  const percentageValue = numericValue <= 1 ? numericValue * 100 : numericValue;
  const clampedValue = Math.min(Math.max(percentageValue, 0), 100);
  const formatted = Number.isInteger(clampedValue) ? clampedValue.toString() : clampedValue.toFixed(2);

  return `${formatted}%`;
};

const stringifyForView = (value: any) => {
  if (value === undefined || value === null) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const formatLabelFromKey = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const asBooleanText = (value: any) => {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (typeof value === "number") {
    if (value === 1) {
      return "Yes";
    }

    if (value === 0) {
      return "No";
    }

    return value.toString();
  }

  const normalized = String(value).trim().toLowerCase();
  if (["true", "yes", "y", "1"].includes(normalized)) {
    return "Yes";
  }

  if (["false", "no", "n", "0"].includes(normalized)) {
    return "No";
  }

  return String(value);
};

type PlaceholderItem = {
  key: string;
  id?: string | number;
  name: string;
  targetName?: string;
  linkedSideApplicable?: any;
};

type ResponseRow = {
  key: string;
  label: string;
  page?: string | number;
  confidence?: any;
  pageType?: string;
};

const inferPageFromText = (value: string) => {
  const match = value.match(/page\s*[-_:]?\s*(\d+)/i) ?? value.match(/^\s*(\d+)\s*$/);
  if (!match) {
    return undefined;
  }

  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const toPercentage = (value: any) => {
  const numericValue = toNumericValue(value);
  if (numericValue === undefined) {
    return undefined;
  }

  const percentageValue = numericValue <= 1 ? numericValue * 100 : numericValue;
  return Math.min(Math.max(percentageValue, 0), 100);
};

const formatBackendConfidence = (value: any) => {
  if (value === undefined || value === null || value === "") {
    return "N/A";
  }

  const numericValue = toNumericValue(value);
  if (numericValue !== undefined) {
    // Truncate to 2 decimal places without rounding
    const truncated = Math.floor(numericValue * 100) / 100;
    return truncated.toFixed(2);
  }

  if (typeof value === "string" || typeof value === "boolean") {
    return String(value);
  }

  return stringifyForView(value);
};

const formatPageTypeDisplay = (value: string) =>
  value
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const formatDisplayTitle = (value: string) => {
  if (value === "N/A") {
    return value;
  }

  return formatPageTypeDisplay(value);
};

const parsePageRange = (value: any): { start: number; end: number } | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return { start: value, end: value };
  }

  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  const singlePageMatch = trimmed.match(/^\d+$/);
  if (singlePageMatch) {
    const page = Number(trimmed);
    return Number.isFinite(page) ? { start: page, end: page } : undefined;
  }

  const rangeMatch = trimmed.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (!rangeMatch) {
    return undefined;
  }

  const start = Number(rangeMatch[1]);
  const end = Number(rangeMatch[2]);
  if (!Number.isFinite(start) || !Number.isFinite(end)) {
    return undefined;
  }

  return {
    start: Math.min(start, end),
    end: Math.max(start, end),
  };
};

const getMeaningfulTitle = (value: any) => {
  if (typeof value !== "string") {
    return "";
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  const normalized = trimmed.toLowerCase();
  if (/^\d+$/.test(trimmed)) {
    return "";
  }

  if (/^page\s*\d+$/i.test(trimmed)) {
    return "";
  }

  if (/^(split|submit)\s*item(\s*\d+)?$/i.test(trimmed)) {
    return "";
  }

  if (normalized === "item") {
    return "";
  }

  return trimmed;
};

const toPageNumber = (value: any) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
};

const buildPageTitleLookup = (sections: any) => {
  const lookup = new Map<number, string>();
  const sectionList = Array.isArray(sections) ? sections : [];

  sectionList.forEach((item) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const title =
      getMeaningfulTitle(item.title) ||
      getMeaningfulTitle(item.section) ||
      getMeaningfulTitle(item.Section) ||
      getMeaningfulTitle(item.placeholderName) ||
      getMeaningfulTitle(item.phDocumentName) ||
      getMeaningfulTitle(item.category) ||
      getMeaningfulTitle(item.Category);

    if (!title) {
      return;
    }

    const range =
      parsePageRange(item.pageRange) ||
      parsePageRange(item.PageRange) ||
      parsePageRange(item.page_range) ||
      (Number.isFinite(item.StartPage) && Number.isFinite(item.EndPage)
        ? {
            start: Math.min(Number(item.StartPage), Number(item.EndPage)),
            end: Math.max(Number(item.StartPage), Number(item.EndPage)),
          }
        : undefined);

    if (!range) {
      const singlePage =
        toPageNumber(item.page) ??
        toPageNumber(item.pageNo) ??
        toPageNumber(item.pageNumber);

      if (singlePage !== undefined && !lookup.has(singlePage)) {
        lookup.set(singlePage, title);
      }

      return;
    }

    for (let page = range.start; page <= range.end; page += 1) {
      if (!lookup.has(page)) {
        lookup.set(page, title);
      }
    }
  });

  return lookup;
};

const buildResponsePageTitleLookup = (responseValue: any) => {
  const lookup = new Map<number, string>();
  const visited = new WeakSet<object>();

  const walk = (node: any, pageHint: number | undefined, depth: number) => {
    if (node === null || node === undefined || depth > 8) {
      return;
    }

    if (typeof node !== "object") {
      return;
    }

    if (visited.has(node)) {
      return;
    }

    visited.add(node);

    if (Array.isArray(node)) {
      node.forEach((item) => walk(item, pageHint, depth + 1));
      return;
    }

    const currentPage =
      toPageNumber(node?.page) ??
      toPageNumber(node?.pageNumber) ??
      toPageNumber(node?.pageNo) ??
      toPageNumber(node?.page_num) ??
      toPageNumber(node?.pageIndex) ??
      pageHint;

    const directTitle =
      getMeaningfulTitle(node?.page_type) ||
      getMeaningfulTitle(node?.pageType) ||
      getMeaningfulTitle(node?.pagetype);

    if (directTitle && currentPage !== undefined && !lookup.has(currentPage)) {
      lookup.set(currentPage, directTitle);
    }

    Object.entries(node).forEach(([key, value]) => {
      const normalizedKey = normalizeLookupKey(key);
      const pageFromKey = inferPageFromText(key);
      const nextPage = pageFromKey ?? currentPage;

      if (typeof value === "string" && ["pagetype"].includes(normalizedKey)) {
        const titleFromEntry = getMeaningfulTitle(value);
        if (titleFromEntry && nextPage !== undefined && !lookup.has(nextPage)) {
          lookup.set(nextPage, titleFromEntry);
        }
      }

      walk(value, nextPage, depth + 1);
    });
  };

  walk(responseValue, undefined, 0);
  return lookup;
};

const resolveRowTitle = (
  row: ResponseRow,
  responsePageTitleLookup: Map<number, string>,
) => {
  const rowTitle = getMeaningfulTitle(row.pageType);
  if (rowTitle) {
    return rowTitle;
  }

  const pageNumber = toPageNumber(row.page);
  if (pageNumber === undefined) {
    return "N/A";
  }

  return (
    responsePageTitleLookup.get(pageNumber) ??
    responsePageTitleLookup.get(pageNumber + 1) ??
    responsePageTitleLookup.get(pageNumber - 1) ??
    "N/A"
  );
};

const extractPlaceholderItems = (placeholderMapping: any): PlaceholderItem[] => {
  if (!placeholderMapping) {
    return [];
  }

  if (Array.isArray(placeholderMapping)) {
    return placeholderMapping.map((item, index) => ({
      key: `placeholder-${index}`,
      id: item?.id ?? item?.placeholderId,
      name:
        item?.name ??
        item?.placeholderName ??
        item?.targetName ??
        `Placeholder ${index + 1}`,
      targetName: item?.targetName ?? item?.phDocumentName,
      linkedSideApplicable:
        item?.applicable_to_linked_side ??
        item?.applicableToLinkedSide ??
        item?.linkedSideApplicable,
    }));
  }

  if (typeof placeholderMapping !== "object") {
    return [];
  }

  return Object.entries(placeholderMapping).map(([key, value], index) => {
    if (value && typeof value === "object" && !Array.isArray(value)) {
      const item = value as any;
      return {
        key: `placeholder-${key}-${index}`,
        id: item.id ?? item.placeholderId,
        name: item.name ?? item.placeholderName ?? formatLabelFromKey(key),
        targetName: item.targetName ?? item.phDocumentName,
        linkedSideApplicable:
          item.applicable_to_linked_side ??
          item.applicableToLinkedSide ??
          item.linkedSideApplicable,
      };
    }

    return {
      key: `placeholder-${key}-${index}`,
      id: undefined,
      name: formatLabelFromKey(key),
      targetName: typeof value === "string" ? value : undefined,
      linkedSideApplicable: undefined,
    };
  });
};

const extractResponseRows = (responseValue: any, prefix: string): ResponseRow[] => {
  if (!responseValue) {
    return [];
  }

  const rows: ResponseRow[] = [];
  const visited = new WeakSet<object>();
  const MAX_ROWS = 200;

  const getIndexedObjectByKeys = (node: any, candidates: string[]) => {
    if (!node || typeof node !== "object" || Array.isArray(node)) {
      return undefined;
    }

    const normalizedCandidates = candidates.map(normalizeLookupKey);
    for (const [key, value] of Object.entries(node)) {
      if (
        normalizedCandidates.includes(normalizeLookupKey(key)) &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        return value as Record<string, any>;
      }
    }

    return undefined;
  };

  const tryGetDirectConfidence = (node: any) =>
    node?.confidenceScore ??
    node?.confidence_score ??
    node?.confidence ??
    node?.score ??
    node?.probability ??
    node?.confidenceLevel;

  const tryGetDirectPage = (node: any) =>
    node?.page ??
    node?.pageNumber ??
    node?.pageNo ??
    node?.page_num ??
    node?.pageIndex;

  const tryGetDirectLabel = (node: any) =>
    node?.title ??
    node?.name ??
    node?.placeholderName ??
    node?.documentName ??
    node?.section ??
    node?.category ??
    node?.targetName ??
    node?.label;

  const tryGetDirectPageType = (node: any) =>
    node?.page_type ??
    node?.pageType ??
    node?.pagetype;

  const pageTypeMap = getIndexedObjectByKeys(responseValue, ["page_type", "pageType", "pagetype"]);
  const confidenceMap = getIndexedObjectByKeys(responseValue, [
    "confidence_scores",
    "confidenceScores",
    "confidence_score",
    "confidenceScore",
    "scores",
  ]);

  const addRow = (
    label: string,
    confidence: any,
    page: string | number | undefined,
    pageType: any,
    rowKey: string,
  ) => {
    if (rows.length >= MAX_ROWS) {
      return;
    }

    rows.push({
      key: rowKey,
      label,
      page,
      confidence,
      pageType: getMeaningfulTitle(pageType),
    });
  };

  const walk = (node: any, fallbackLabel: string, pageHint: string | number | undefined, depth: number) => {
    if (rows.length >= MAX_ROWS || node === null || node === undefined || depth > 8) {
      return;
    }

    if (typeof node !== "object") {
      const confidencePercentage = toPercentage(node);
      if (confidencePercentage !== undefined) {
        const pageFromLabel = inferPageFromText(fallbackLabel);
        addRow(
          fallbackLabel,
          node,
          pageHint ?? pageFromLabel,
          undefined,
          `${prefix}-${fallbackLabel}-${rows.length}`,
        );
      }
      return;
    }

    if (visited.has(node)) {
      return;
    }
    visited.add(node);

    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        walk(item, `${fallbackLabel} ${index + 1}`, pageHint, depth + 1);
      });
      return;
    }

    const directConfidence = tryGetDirectConfidence(node);
    const directLabel = tryGetDirectLabel(node);
    const directPage = tryGetDirectPage(node);
    const directPageType = tryGetDirectPageType(node);

    if (directConfidence !== undefined) {
      addRow(
        String(directLabel ?? fallbackLabel),
        directConfidence,
        directPage ?? pageHint ?? inferPageFromText(fallbackLabel),
        directPageType,
        `${prefix}-${String(directLabel ?? fallbackLabel)}-${rows.length}`,
      );
      return;
    }

    Object.entries(node).forEach(([key, value]) => {
      const nextLabel = formatLabelFromKey(key);
      const pageFromKey = inferPageFromText(key);
      const nextPageHint = pageFromKey ?? pageHint;

      if (value === null || value === undefined) {
        return;
      }

      if (typeof value !== "object") {
        const confidencePercentage = toPercentage(value);
        if (confidencePercentage !== undefined) {
          addRow(
            nextLabel,
            value,
            nextPageHint,
            undefined,
            `${prefix}-${nextLabel}-${rows.length}`,
          );
        }
        return;
      }

      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          walk(item, `${nextLabel} ${index + 1}`, nextPageHint, depth + 1);
        });
        return;
      }

      const nestedDirectConfidence = tryGetDirectConfidence(value);
      if (nestedDirectConfidence !== undefined) {
        const nestedLabel = tryGetDirectLabel(value) ?? nextLabel;
        const nestedPage = tryGetDirectPage(value) ?? nextPageHint;
        const nestedPageType = tryGetDirectPageType(value);
        addRow(
          String(nestedLabel),
          nestedDirectConfidence,
          nestedPage,
          nestedPageType,
          `${prefix}-${String(nestedLabel)}-${rows.length}`,
        );
        return;
      }

      walk(value, nextLabel, nextPageHint, depth + 1);
    });
  };

  if (pageTypeMap || confidenceMap) {
    const pageKeys = Array.from(
      new Set([...(pageTypeMap ? Object.keys(pageTypeMap) : []), ...(confidenceMap ? Object.keys(confidenceMap) : [])]),
    );

    pageKeys
      .sort((a, b) => {
        const numA = toPageNumber(a);
        const numB = toPageNumber(b);
        if (numA !== undefined && numB !== undefined) {
          return numA - numB;
        }

        if (numA !== undefined) {
          return -1;
        }

        if (numB !== undefined) {
          return 1;
        }

        return a.localeCompare(b);
      })
      .forEach((pageKey, index) => {
        const pageTypeValue = pageTypeMap?.[pageKey];
        const confidenceValue = confidenceMap?.[pageKey];
        const pageTypeLabel = getMeaningfulTitle(pageTypeValue);
        const pageNumber = toPageNumber(pageKey);

        addRow(
          pageTypeLabel || formatLabelFromKey(pageKey),
          confidenceValue,
          pageNumber ?? pageKey,
          pageTypeValue,
          `${prefix}-indexed-${pageKey}-${index}`,
        );
      });

    return rows.slice(0, MAX_ROWS);
  }

  walk(responseValue, `${formatLabelFromKey(prefix)} Item`, undefined, 0);

  const deduplicatedRows = Array.from(
    new Map(rows.map((item) => [`${item.page ?? ""}-${item.pageType ?? ""}-${item.label}-${item.confidence ?? ""}`, item])).values(),
  );

  return deduplicatedRows.slice(0, MAX_ROWS);
};

const HEADINGS = {
  DOCUMENT_INSIGHTS: "DOCUMENT INSIGHTS",
  DOCUMENT_SPLIT_SUMMARY: "Dcoument Summary",

};


export const DocumentContainer = () => {
  const MIN_LEFT_WIDTH = 360;
  const MIN_RIGHT_WIDTH = 420;
  const DIVIDER_WIDTH = 24;
  const containerRef = useRef<HTMLDivElement>(null);
  const [pdfViewer, setPdfViewer] = useState({ signedUrl: "", currentPage: 1 });
  const [activeTab, setActiveTab] = useState("1");
  const [mappingView, setMappingView] = useState<"structured" | "raw">("structured");
  const [leftPaneWidth, setLeftPaneWidth] = useState(0);
  const [isResizing, setIsResizing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<SplitDataItem>();
  const { documentReview, sections, placeholderMapping, incomingMessage, refreshSections } = useDocumentReview();

  const splitData = sections ?? [];
  const mappingData = placeholderMapping ?? {};
  const kafkaMessage = incomingMessage;

  // Set initial selected category when sections load
  useEffect(() => {
    if (splitData.length > 0 && !selectedCategory) {
      const first = splitData[0];
      setSelectedCategory({
        key: 0,
        section: first.Section,
        category: first.Category,
        pageRange: first.PageRange,
        feedbackStatus: first.feedbackStatus,
        insights: first.insights,
        sectionId: first.sectionId,
        confidenceScore: first.confidenceScore,
        noOfCommentsInsight: first.noOfCommentsInsight,
        noOfCommentsSection: first.noOfCommentsSection,
      });
    }
  }, [splitData]);

  const getSections = refreshSections;

  useEffect(() => {
    const setInitialWidth = () => {
      if (!containerRef.current || leftPaneWidth > 0) {
        return;
      }

      const containerWidth = containerRef.current.clientWidth;
      const defaultWidth = Math.round(containerWidth * 0.42);
      const maxLeftWidth = containerWidth - MIN_RIGHT_WIDTH - DIVIDER_WIDTH;
      setLeftPaneWidth(Math.max(MIN_LEFT_WIDTH, Math.min(defaultWidth, maxLeftWidth)));
    };

    setInitialWidth();
  }, [leftPaneWidth]);

  const handlePdfChange = (key: string, value: string | number) =>
    setPdfViewer((prev) => ({ ...prev, [key]: value }));

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
  };

  const handleResizeStart = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      return;
    }

    const getClientX = (event: MouseEvent | TouchEvent) => {
      if ("touches" in event) {
        return event.touches[0]?.clientX ?? 0;
      }

      return event.clientX;
    };

    const handleResizeMove = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current) {
        return;
      }

      const containerRect = containerRef.current.getBoundingClientRect();
      const maxLeftWidth = containerRect.width - MIN_RIGHT_WIDTH - DIVIDER_WIDTH;
      const nextWidth = getClientX(event) - containerRect.left;
      const boundedWidth = Math.max(MIN_LEFT_WIDTH, Math.min(nextWidth, maxLeftWidth));

      setLeftPaneWidth(boundedWidth);
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
    };

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    window.addEventListener("mousemove", handleResizeMove);
    window.addEventListener("mouseup", handleResizeEnd);
    window.addEventListener("touchmove", handleResizeMove, { passive: false });
    window.addEventListener("touchend", handleResizeEnd);

    return () => {
      window.removeEventListener("mousemove", handleResizeMove);
      window.removeEventListener("mouseup", handleResizeEnd);
      window.removeEventListener("touchmove", handleResizeMove);
      window.removeEventListener("touchend", handleResizeEnd);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing]);

  useEffect(() => {
    if (selectedCategory?.pageRange) {
      setPdfViewer((prev) => ({
        ...prev,
        currentPage:
          parseInt(selectedCategory.pageRange?.split("-")[0]?.trim(), 10) || 1,
      }));
    }
  }, [selectedCategory?.pageRange]);
  // console.log(selectedCategory);

  const renderEvidenceContentJSON = () => {
    const extractionJsonOutput =
      mappingData?.rawJson ??
      mappingData?.rawJSON ??
      mappingData?.json ??
      mappingData?.extractionResult ??
      mappingData?.extractionResults ??
      (Array.isArray(mappingData) && mappingData.length === 0
        ? documentReview.tolstoyJson
        : mappingData);

    const splitSubmitContainer =
      findValueByKeyCandidates(mappingData, ["splitSubmitResponse", "split_submit_response", "splitAndSubmitResponse", "response4in1", "response4In1"]) ??
      findValueByKeyCandidates(extractionJsonOutput, ["splitSubmitResponse", "split_submit_response", "splitAndSubmitResponse", "response4in1", "response4In1"]);

    const splitResponseOutput =
      findValueByKeyCandidates(mappingData, ["splitResponse", "split_response", "splitResult", "split_results"]) ??
      findValueByKeyCandidates(extractionJsonOutput, ["splitResponse", "split_response", "splitResult", "split_results"]) ??
      (splitSubmitContainer && typeof splitSubmitContainer === "object"
        ? findValueByKeyCandidates(splitSubmitContainer, ["splitResponse", "split_response", "splitResult", "split_results", "split"])
        : undefined);

    const submitResponseOutput =
      findValueByKeyCandidates(mappingData, ["submitResponse", "submit_response", "submitResult", "submit_results"]) ??
      findValueByKeyCandidates(extractionJsonOutput, ["submitResponse", "submit_response", "submitResult", "submit_results"]) ??
      (splitSubmitContainer && typeof splitSubmitContainer === "object"
        ? findValueByKeyCandidates(splitSubmitContainer, ["submitResponse", "submit_response", "submitResult", "submit_results", "submit"])
        : undefined);

    const documentIdOutput =
      findValueByKeyCandidates(mappingData, ["documentId", "documentUUID", "documentUuid"]) ??
      findValueByKeyCandidates(extractionJsonOutput, ["documentId", "documentUUID", "documentUuid"]);

    const placeholderMappingOutput =
      findValueByKeyCandidates(mappingData, ["placeholderMapping", "placeholder_mapping", "identifiedPlaceholders"]) ??
      findValueByKeyCandidates(extractionJsonOutput, ["placeholderMapping", "placeholder_mapping", "identifiedPlaceholders"]);

    const placeholderItems = extractPlaceholderItems(placeholderMappingOutput);
    const splitRows = extractResponseRows(splitResponseOutput, "split");
    const submitRows = extractResponseRows(submitResponseOutput, "submit");
    const splitResponsePageTitleLookup = buildResponsePageTitleLookup(splitResponseOutput);
    const submitResponsePageTitleLookup = buildResponsePageTitleLookup(submitResponseOutput);

    // Build combined split+submit table rows
    const splitPageTypes = splitResponseOutput?.page_type ?? splitResponseOutput?.pageType ?? {};
    const splitConfScores = splitResponseOutput?.confidence_scores ?? splitResponseOutput?.confidenceScores ?? {};
    const submitPageTypes = submitResponseOutput?.page_type ?? submitResponseOutput?.pageType ?? {};
    const submitConfScores = submitResponseOutput?.confidence_scores ?? submitResponseOutput?.confidenceScores ?? {};

    const splitPageKeys = Object.keys(splitPageTypes).sort((a, b) => Number(a) - Number(b));
    const submitPageKeys = Object.keys(submitPageTypes).sort((a, b) => Number(a) - Number(b));

    // Detect title pages from split response: pages classified as "Title" by the split model
    const titlePageIndices: number[] = [];
    splitPageKeys.forEach((key) => {
      const pageType = (splitPageTypes[key] ?? "").toString().trim().toLowerCase();
      if (pageType === "title") {
        titlePageIndices.push(Number(key));
      }
    });

    // Map submit entries (sequential 0,1,2...) to actual document title page numbers
    const submitByActualPage = new Map<number, { type: string; conf: any }>();
    submitPageKeys.forEach((submitKey, idx) => {
      const actualPage = titlePageIndices[idx];
      if (actualPage !== undefined) {
        submitByActualPage.set(actualPage, {
          type: submitPageTypes[submitKey],
          conf: submitConfScores[submitKey],
        });
      }
    });

    const combinedTableRows = splitPageKeys.map((key) => {
      const pageNum = Number(key);
      const submit = submitByActualPage.get(pageNum);
      return {
        page: pageNum,
        splitType: splitPageTypes[key] ?? null,
        splitConf: splitConfScores[key] ?? null,
        submitType: submit?.type ?? null,
        submitConf: submit?.conf ?? null,
        isTitle: titlePageIndices.includes(pageNum),
      };
    });

    const hasCombinedTableData = combinedTableRows.length > 0;

    const confidenceSummary = [
      {
        label: "Section Confidence",
        value: selectedCategory?.confidenceScore,
      },
      {
        label: "Overall Confidence",
        value:
          findValueByKeyCandidates(mappingData, ["overallConfidence", "confidenceScore", "confidence_score", "confidence"]) ??
          findValueByKeyCandidates(extractionJsonOutput, ["overallConfidence", "confidenceScore", "confidence_score", "confidence"]),
      },
      {
        label: "Split Confidence",
        value:
          findValueByKeyCandidates(splitResponseOutput, ["confidenceScore", "confidence", "score"]) ??
          findValueByKeyCandidates(mappingData, ["splitConfidence", "split_confidence"]),
      },
      {
        label: "Submit Confidence",
        value:
          findValueByKeyCandidates(submitResponseOutput, ["confidenceScore", "confidence", "score"]) ??
          findValueByKeyCandidates(mappingData, ["submitConfidence", "submit_confidence"]),
      },
    ].filter((item) => item.value !== undefined && item.value !== null && item.value !== "");

    const hasRawExtractionData =
      extractionJsonOutput !== undefined &&
      extractionJsonOutput !== null &&
      (typeof extractionJsonOutput !== "object" ||
        Array.isArray(extractionJsonOutput) ||
        Object.keys(extractionJsonOutput).length > 0);

    const hasStructuredData =
      Boolean(documentIdOutput) ||
      placeholderItems.length > 0 ||
      splitRows.length > 0 ||
      submitRows.length > 0 ||
      hasCombinedTableData ||
      confidenceSummary.length > 0;

    if(!documentReview.tolstoyJson && !hasStructuredData && !hasRawExtractionData){
      return (
        <div className="flex flex-column gp jcc aic">

          <Empty description={"No Data Found"} />
          <p>No Data Found</p>
        </div>
    )
    }else{
      return (
        <Tabs
          activeKey={activeTab}
          defaultActiveKey="1"
          onChange={handleTabChange}
          tabBarGutter={25}
          size="large"
          className="point-tabs-container"
        >
          <TabPane tab={<span>Final Response </span>} key={"1"}>
            <div className="mapping-results-container">
              <EvidenceContentJSON pageInfo={documentReview.tolstoyJson} />
            </div>
          </TabPane>
          <TabPane tab={<span>Extraction Results </span>} key={"2"}>
            <div className="mapping-results-container">
              <div className="mapping-results-header">
                <div>
                  <h4 className="mapping-results-title">
                    <TableOutlined /> {mappingView === "structured" ? "Model Results" : "Mapping Results"}
                  </h4>
                  <p className="mapping-results-subtitle">
                   {mappingView === "structured"
                      ? "Structured model output"
                      : "Model output mapped to placeholders"}
                  </p>
                </div>
                <div className="mapping-results-toggle">
                  <button
                    type="button"
                    className={mappingView === "structured" ? "active" : ""}
                    onClick={() => setMappingView("structured")}
                  >
                  <DatabaseFilled/>
                  </button>
                  <button
                    type="button"
                    className={mappingView === "raw" ? "active" : ""}
                    onClick={() => setMappingView("raw")}
                  >
                    {'{}'}
                  </button>
                </div>
              </div>
              {mappingView === "structured" ? (
                <div className="mapping-structured-summary">
                  {hasCombinedTableData ? (
                    <div className="ss-table-card">
                      <table className="ss-table">
                        <thead>
                          <tr>
                            <th className="ss-table-th">Page</th>
                            <th className="ss-table-th">Split </th>
                            <th className="ss-table-th ss-table-th--score">
                              Conf. Score
                            </th>
                            <th className="ss-table-th">Submit</th>
                            <th className="ss-table-th ss-table-th--score">
                              Conf. Score
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {combinedTableRows.map((row) => (
                            <tr
                              key={row.page}
                              className={`ss-table-row ${row.isTitle ? "ss-table-row--title" : ""}`}
                            >
                              <td className="ss-table-td">
                                <span className="ss-conf-value">
                                  {row.page}
                                </span>
                              </td>
                              <td className="ss-table-td ss-table-td--type">
                                {row.splitType ? (
                                  formatPageTypeDisplay(row.splitType)
                                ) : (
                                  <span className="ss-empty">—</span>
                                )}
                              </td>
                              <td className="ss-table-td ss-table-td--score">
                                {row.splitConf !== null ? (
                                  <span className="ss-conf-value">
                                    {formatBackendConfidence(row.splitConf)}
                                  </span>
                                ) : (
                                  <span className="ss-empty">—</span>
                                )}
                              </td>
                              <td className="ss-table-td ss-table-td--type">
                                {row.submitType ? (
                                  <span className="ss-submit-value">
                                    {formatPageTypeDisplay(row.submitType)}
                                  </span>
                                ) : (
                                  <span className="ss-empty">—</span>
                                )}
                              </td>
                              <td className="ss-table-td ss-table-td--score">
                                {row.submitConf !== null ? (
                                  <span className="ss-conf-value">
                                    {formatBackendConfidence(row.submitConf)}
                                  </span>
                                ) : (
                                  <span className="ss-empty">—</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="response-empty-text">
                      No split/submit response available
                    </p>
                  )}
                </div>
              ) : hasRawExtractionData ? (
                <EvidenceContentJSON pageInfo={extractionJsonOutput} />
              ) : (
                <div className="mapping-results-empty">
                  <Empty description="No raw JSON available" />
                </div>
              )}
            </div>
          </TabPane>
          <TabPane tab={<span>Incoming Message </span>} key={"3"}>
            <div className="mapping-results-container">
              <IncomingContentJSON
                pageInfo={{ incomingMessage: kafkaMessage }}
              />
            </div>
          </TabPane>
        </Tabs>
      );
    }
  };

  return (
    <div
      ref={containerRef}
      className="document-page-container"
      style={{
        gridTemplateColumns:
          leftPaneWidth > 0
            ? `${leftPaneWidth}px ${DIVIDER_WIDTH}px minmax(${MIN_RIGHT_WIDTH}px, 1fr)`
            : undefined,
      }}
    >
      <div className="json-section">{renderEvidenceContentJSON()}</div>
      <div
        className={`drawer ${isResizing ? "resizing" : ""}`}
        onMouseDown={handleResizeStart}
        onTouchStart={handleResizeStart}
      >
        <div className="slide-bar">
          <button className="slide-grip" type="button" aria-label="Section divider" />
        </div>
      </div>
      <div className="collapse gp flex flex-column">
              <Collapse
                activeKey={HEADINGS.DOCUMENT_SPLIT_SUMMARY}
              >
                <Collapse.Panel
                  showArrow={false}
                  collapsible="disabled"
                  header={(
                    <span className="split-summary-header">
                      <FileOutlined className="split-summary-header-icon" />
                      <span className="split-summary-header-title">
                        {HEADINGS.DOCUMENT_SPLIT_SUMMARY}
                      </span>
                    </span>
                  )}
                  key={HEADINGS.DOCUMENT_SPLIT_SUMMARY}
                >
                  <SplitSummary
                    splitData={splitData}
                    setSelectedCategory={setSelectedCategory}
                    pdfUrl={pdfViewer.signedUrl}
                    getSections={getSections}
                  />

                </Collapse.Panel>
              </Collapse>
              
              {/* <Collapse
                accordion
                expandIconPosition="end"
                expandIcon={(e) =>
                  e.isActive ? (
                    <PaginationLeftArrowIcon />
                  ) : (
                    <PaginationRightArrowIcon />
                  )
                }
                defaultActiveKey={HEADINGS.DOCUMENT_INSIGHTS}
              >
                <Collapse.Panel
                  header={HEADINGS.DOCUMENT_INSIGHTS}
                  key={HEADINGS.DOCUMENT_INSIGHTS}
                >
                  <Insights
                    selectedCategory={selectedCategory}
                    sectionId={selectedCategory.sectionId}
                    getSections={getSections}
                  />
                </Collapse.Panel>
              </Collapse> */}
            </div>
    </div>
  );
};
